import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, Bus, Footprints, Loader2, MapPin, Phone, ShieldAlert, TrainFront,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import PolicyLink from "@/components/PolicyLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { EMERGENCY_SITUATIONS } from "@/config/journeyCatalog";

const SEGMENT_ICONS = { walk: Footprints, bus: Bus, train: TrainFront, underground: TrainFront, tram: Bus, transfer: MapPin, other: MapPin };

export default function TrainerJourneyView() {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [denied, setDenied] = useState(false);
  const [direction, setDirection] = useState("outbound");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/journey-plans/${planId}/trainer-view`)
      .then((r) => setPlan(r.data))
      .catch((err) => {
        if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      });
  }, [planId]);

  if (denied) {
    return (
      <div data-testid="trainer-view-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldAlert className="h-10 w-10 text-red-600 mb-4" />
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-sm text-slate-600">You are not authorised to view this route plan.</p>
      </div>
    );
  }

  if (!plan) {
    return <div className="flex items-center gap-2 text-slate-600 py-10" data-testid="trainer-view-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading journey…</div>;
  }

  const sendToCoordinator = async () => {
    setSending(true);
    try {
      await api.post("/report-submissions", { report_type: "journey_plan", report_id: planId });
      toast.success("Route plan sent to the Co-ordinator");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const segments = plan[direction] || [];
  const emergencyEntries = EMERGENCY_SITUATIONS.filter((s) => {
    const e = plan.emergency_plan?.[s.key];
    return e && (e.expected_action || e.who_to_contact);
  });

  return (
    <div data-testid="trainer-journey-view" className="max-w-xl mx-auto space-y-4 pb-10">
      <Link to={`/journey-plans/${planId}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="trainer-view-back">
        <ArrowLeft className="h-4 w-4" /> Full plan
      </Link>

      <Card>
        <CardContent className="p-4">
          <p className="font-heading text-xl font-bold text-slate-900" data-testid="trainer-child-name">{plan.child?.child_name}</p>
          <p className="text-xs text-slate-500 font-mono">{plan.case_id} · {plan.plan_number} v{plan.version}</p>
          <p className="text-sm text-slate-600">{plan.child?.school_name || ""}</p>
          <div className="mt-1"><PolicyLink module="journeys" /></div>
          <Button variant="outline" className="mt-3 h-11 w-full" data-testid="trainer-send-coordinator" disabled={sending} onClick={sendToCoordinator}>
            {sending ? "Sending…" : "Send route plan to Co-ordinator"}
          </Button>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {[["outbound", "Home → School"], ["return", "School → Home"]].map(([k, label]) => (
              <button key={k} data-testid={`trainer-direction-${k}`} onClick={() => setDirection(k)}
                className={`rounded-md px-3 py-3 text-sm font-semibold transition-colors duration-150 min-h-[48px] ${
                  direction === k ? "bg-primary text-white" : "bg-slate-100 text-slate-700"
                }`}>
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="rounded-md border border-border bg-slate-100 px-3 py-2.5 text-center text-sm font-bold text-slate-700">
        {direction === "outbound" ? "HOME" : "SCHOOL"}
      </div>

      {segments.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-6" data-testid="trainer-view-empty">No segments recorded for this direction yet.</p>
      )}

      {segments.map((s, i) => {
        const Icon = SEGMENT_ICONS[s.type] || MapPin;
        return (
          <div key={s.id || i} data-testid={`trainer-segment-${i}`}>
            <div className="flex justify-center text-slate-400 leading-none">↓</div>
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-light">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 capitalize">
                      {s.type}{s.route_number ? ` ${s.route_number}` : ""}
                    </p>
                    <p className="text-xs text-slate-600">
                      {s.boarding ? `${s.boarding} → ${s.alighting || "?"}` : `${s.start_point || "?"} → ${s.end_point || "?"}`}
                      {s.estimated_duration ? ` · ~${s.estimated_duration}` : ""}
                    </p>
                  </div>
                </div>
                {s.instructions && (
                  <p className="text-sm text-slate-800 bg-brand-light/50 rounded-md p-2.5">{s.instructions}</p>
                )}
                {(s.landmarks || []).map((l, li) => (
                  <div key={li} className="flex items-start gap-2 text-sm text-slate-700">
                    <MapPin className="h-4 w-4 text-ukgreen shrink-0 mt-0.5" />
                    <span><strong>{l.name}</strong>{l.instruction ? ` — ${l.instruction}` : ""}</span>
                  </div>
                ))}
                {(s.crossings || []).map((c, ci) => (
                  <div key={ci} className="flex items-start gap-2 text-sm text-slate-700">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <span><strong>{(c.crossing_type || "crossing").replace(/_/g, " ")}</strong> at {c.location}{c.trainer_instruction ? ` — ${c.trainer_instruction}` : ""}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        );
      })}

      <div className="rounded-md border border-border bg-slate-100 px-3 py-2.5 text-center text-sm font-bold text-slate-700">
        {direction === "outbound" ? "SCHOOL" : "HOME"}
      </div>

      {plan.risk_controls?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-600" /> Key risk controls</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {plan.risk_controls.map((c, i) => (
              <p key={i} className="text-sm text-slate-700" data-testid={`trainer-control-${i}`}>
                <strong>{c.hazard}:</strong> {c.control}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {emergencyEntries.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Phone className="h-4 w-4 text-red-600" /> Emergency plan</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {emergencyEntries.map((s) => {
              const e = plan.emergency_plan[s.key];
              return (
                <div key={s.key} className="rounded-md border border-red-200 bg-red-50 p-3" data-testid={`trainer-emergency-${s.key}`}>
                  <p className="text-sm font-bold text-red-900">{s.label}</p>
                  {e.expected_action && <p className="text-sm text-red-800 mt-0.5">{e.expected_action}</p>}
                  {e.who_to_contact && (
                    <p className="text-sm text-red-900 font-semibold mt-1">Contact: {e.who_to_contact}{e.backup_contact ? ` · backup: ${e.backup_contact}` : ""}</p>
                  )}
                </div>
              );
            })}
            {plan.emergency_contacts_note && (
              <p className="text-xs text-slate-600">{plan.emergency_contacts_note}</p>
            )}
          </CardContent>
        </Card>
      )}

      <Badge variant="secondary" className="text-xs">Plan status: {(plan.status || "").replace(/_/g, " ")}</Badge>
    </div>
  );
}
